import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import editGreenIcon from 'assets/images/icons/edit-green.svg';
import selectArrow from 'assets/images/icons/arrow-down.svg';
import ColorChangeModal from 'components/common-components/ColorChangeModal';
import Error from 'components/Error';
import SelectField from 'components/form-components/SelectField';
import SelectIconField from 'components/form-components/SelectIconField';
import TextArea from 'components/form-components/TextArea';
import TextField from 'components/form-components/TextField';
import { isPermissionAvailable } from 'config/permissionUtils';
import { permissionKeys } from 'config/config';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';

function Form({
  values,
  errors,
  touched,
  handleChange,
  APIError,
  folderList,
  companyFieldList,
  isFormSaved,
}) {
  const { t } = useTranslation();
  const [openColorModal, setOpenColorModal] = useState(false);

  const textDirectionValues = [
    { value: 'left_to_right', displayValue: 'select_left_to_right' },
    { value: 'right_to_left', displayValue: 'select_right_to_left' },
  ];
  const textDirectionValuesMap = Object.fromEntries(
    textDirectionValues.map((v) => [v.value, v.displayValue]),
  );

  const folderListValues = [
    { value: null, displayValue: 'select_no_folder' },
    ...folderList.map((f) => ({ value: f._id, displayValue: f.name })),
  ];
  const folderListValuesMap = Object.fromEntries(
    folderListValues.map((v) => [v.value, v.displayValue]),
  );

  const handleCompanyFieldChange = (fieldId, isChecked) => {
    let tempValues = [...values.companyFields];
    if (isChecked) tempValues.push(fieldId);
    else tempValues = tempValues.filter((id) => id !== fieldId);
    handleChange('companyFields', tempValues);
  };

  return (
    <div className='pb-3'>
      {APIError && <Error msg={APIError} />}
      <TextField
        name='title'
        displayName='field_form_title'
        placeholder='field_form_title'
        value={values.title}
        error={errors.title}
        touched={touched.title}
        handleChange={handleChange}
        classes='mb-3 w-100'
        shrinkFieldOnly
        horizontalLabelClass={'min-width-176'}
      />
      <TextArea
        name='description'
        displayName='field_description'
        value={values.description}
        handleChange={handleChange}
        classes='mb-3 w-100'
        rows={3}
        horizontalLabelClass={'min-width-176'}
      />
      <SelectField
        name='folder'
        handleChange={handleChange}
        listValues={folderListValues}
        placeholder='field_form_folder'
        label={folderListValuesMap[values.folder] ?? 'select_select'}
        icon={selectArrow}
        variant='white-1'
        menuClasses='w-100'
        isAutoClose={true}
        classes={
          !isPermissionAvailable(permissionKeys.field_work_form_permissions_create)
            ? 'pe-none opacity-50 mb-3'
            : 'mb-3'
        }
      />
      <div className='row gx-3 gy-2'>
        <div className='col-md-4'>
          <SelectField
            name='textDirection'
            handleChange={handleChange}
            listValues={textDirectionValues}
            placeholder='field_form_direction'
            label={textDirectionValuesMap[values.textDirection] ?? 'select_select'}
            icon={selectArrow}
            variant='white-1'
            menuClasses='w-100'
            isAutoClose={true}
            filedClasses='w-100'
          />
        </div>
        <div className='col-md-3'>
          <SelectIconField
            handleChange={handleChange}
            name='formIcon'
            placeholder='field_form_icon'
            label={values.formIcon}
            icon={selectArrow}
            variant='white-1'
            isAutoClose={true}
            iconType='form'
            classes='w-100'
            menuClasses='w-100'
          />
        </div>
        <div className='col-md-5'>
          <label className='min-width-176'>{t('text_submit_button_color')}</label>
          <div>
            <div
              className='w-100 d-flex justify-content-center align-items-center gap-3 cursor-pointer rounded-sm text-center theme-text-white-1 theme-height-44 px-2'
              style={{ backgroundColor: values.submitButtonColor }}
              onClick={() => setOpenColorModal(true)}
            >
              {t('text_preview_submit')}
              <img src={editGreenIcon} className='svg-white' />
            </div>
            <div className='position-relative'>
              {openColorModal && (
                <ColorChangeModal
                  name='submitButton'
                  closeModal={() => setOpenColorModal(false)}
                  styleProperties={values}
                  handleChange={handleChange}
                  hideFontSizeOption
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <label className='mt-3'>{t('field_company_fields')}</label>
      <div className='d-flex gap-2 align-items-center border-grey-1-h-1 rounded-sm mt-2 p-3'>
        {companyFieldList.length > 0 ? (
          <>
            <div className='w-50'>
              <div className='d-flex flex-column gap-2'>
                {companyFieldList.map((field, index) => (
                  <div key={index} className='d-flex align-items-center gap-2'>
                    <SingleCheckBoxField
                      name={field._id}
                      value={values.companyFields.includes(field._id)}
                      text={<span>{field.fieldTitle}</span>}
                      handleChange={handleCompanyFieldChange}
                      disabled={!isFormSaved}
                    />
                  </div>
                ))}
              </div>
            </div>
            {!isFormSaved && (
              <div className='w-50 d-flex flex-column justify-content-center align-items-center text-center'>
                <label className='fw-semibold'>{t('text_save_form_exclaim')}</label>
                <label>{t('text_company_field_requires_form_saved')}</label>
              </div>
            )}
          </>
        ) : (
          <label className='w-100 text-center'>{t('text_no_company_field_found')}</label>
        )}
      </div>
    </div>
  );
}
Form.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  APIError: PropTypes.string.isRequired,
  folderList: PropTypes.array.isRequired,
  companyFieldList: PropTypes.array.isRequired,
  isFormSaved: PropTypes.bool.isRequired,
};
export default Form;
